import type { Connection, NodeTypes, NodeChange } from '@xyflow/react';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ReactFlow, useReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme, keyframes, css } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { Label } from '@components/Label';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import DotBG from '@images/workflow_BG.png';
import type { WorkflowExecStatus } from '@repositories/workflowRepository/workflow.types';
import type { WorkflowExecModel } from '@repositories/workflowRepository/models/WorkflowExecModel';
import type { WorkflowModel } from '@repositories/workflowRepository/models/WorkflowModel';
import { ActionNode } from './ActionNode';
import { ConditionNode } from './ConditionNode';
import { ConditionThenEdge } from './ConditionThenEdge';
import { EndNode } from './EndNode';
import { ExceptionEdge } from './ExceptionEdge';
import { NormalEdge } from './NormalEdge';
import { TriggerNode } from './TriggerNode';

const edgeTypes = {
  normal: NormalEdge, // SmoothStep, '+' 버튼 포함
  exception: ExceptionEdge, // SmoothStep, 직각, "Exception" 레이블
  conditionThen: ConditionThenEdge, // SmoothStep, 직각, "True" 레이블
};

// Node types cast for v12 compatibility - using 'as NodeTypes' to allow custom node components
const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  end: EndNode,
} as NodeTypes;

interface Props {
  workflow?: WorkflowModel;
  execution?: WorkflowExecModel;
}

const isBlinkingStatus = (status: WorkflowExecStatus) => {
  return status === 'READY' || status === 'RUNNING' || status === 'STOP_REQUESTED';
};

const WorkflowViewerContent = observer(({ workflow, execution }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const isCreate = location.pathname.includes('create');
  const workflowViewerRef = useRef<HTMLDivElement | null>(null);
  const { fitView } = useReactFlow();

  const {
    workflowDetailStore,
    uiStore: { toastStore },
  } = useStore();
  const isEdit = workflowDetailStore.isEdit;
  const isEditMode = workflowDetailStore.checkEditable();
  // 노드 위치 조정 (중앙 정렬)
  const nodes = workflowDetailStore.nodes.map(node => ({
    ...node,
    position: { x: (workflowViewerRef?.current?.offsetWidth ?? 0) / 3 - 196 + node.position.x, y: node.position.y },
  }));

  // ReactFlow 컨테이너 ref를 store에 저장
  // SelectNodeSpeech 메뉴 위치 계산을 위해 사용
  useLayoutEffect(() => {
    const reactFlowContainer = workflowViewerRef.current?.querySelector('.react-flow') as HTMLDivElement;
    if (reactFlowContainer) {
      workflowDetailStore.reactFlowContainerRef = reactFlowContainer;
    }
  }, [workflowDetailStore]);

  useLayoutEffect(() => {
    if (!isCreate && !workflow) return;

    workflowDetailStore.initNodes(isCreate || isEdit, workflow, execution);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, workflow]);

  /**
   * 노드 연결 처리
   * @param connection 연결 정보
   */
  const handleConnect = (connection: Connection) => {
    const result = workflowDetailStore.connectNodes(connection);
    if (!result.success && result.errorKey) {
      toastStore.open({ message: t(result.errorKey) });
    }
  };

  /**
   * 노드 변경 처리 (드래그 등)
   */
  const handleNodesChange = (changes: NodeChange[]) => {
    // position 변경만 처리 (드래그)
    const positionChanges = changes.filter(
      (change): change is NodeChange & { type: 'position'; position: { x: number; y: number }; dragging: boolean } =>
        change.type === 'position' && 'position' in change && change.position !== undefined,
    );

    // 드래그 중인 노드들의 위치 업데이트
    if (positionChanges.length > 0) {
      const updates = positionChanges.map(change => ({
        id: change.id,
        position: {
          // WorkflowViewer에서 적용한 x 오프셋을 역으로 제거하여 원래 위치 저장
          x: change.position.x - ((workflowViewerRef?.current?.offsetWidth ?? 0) / 3 - 196),
          y: change.position.y,
        },
      }));
      workflowDetailStore.updateNodesPosition(updates);
    }
  };

  /**
   * 배경 클릭 처리
   */
  const handlePaneClick = () => {
    if (!isEditMode && !workflowDetailStore.execution) {
      workflowDetailStore.curNodeId = '';
      if (workflowDetailStore.toolbar === '') {
        workflowDetailStore.setToolbarView('workflowInfo');
      } else {
        workflowDetailStore.setToolbarView('');
      }
    }
  };

  const handleFitView = () => {
    fitView({ padding: 0.2, duration: 500 });
  };

  return (
    <Viewer ref={workflowViewerRef}>
      <StyledReactFlow
        nodes={nodes}
        edges={workflowDetailStore.edges.map(edge => ({
          ...edge,
          style: { stroke: theme.colors['border-gray-light'] },
        }))}
        nodeTypes={nodeTypes} // 커스텀 노드 타입
        edgeTypes={edgeTypes} // 커스텀 엣지 타입
        onConnect={handleConnect} // 노드 연결 처리
        onNodesChange={handleNodesChange} // 노드 변경 처리 (드래그 등)
        onPaneClick={handlePaneClick} // 배경 클릭 처리
        nodesDraggable={isEditMode}
        nodesConnectable={isEditMode}
        elementsSelectable
        selectNodesOnDrag={false}
        minZoom={0.1}
        maxZoom={1.5}
      />
      {/* 전체 보기 버튼 */}
      <FitViewButton onClick={handleFitView} title="전체 보기">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M3 3h7v2H5v5H3V3zm18 0h-7v2h5v5h2V3zM3 21v-7h2v5h5v2H3zm18 0h-7v-2h5v-5h2v7z" fill="currentColor" />
        </svg>
      </FitViewButton>
      {/* 편집 모드 라벨 */}
      {isEdit && (
        <EditModeLabel variant="outline" color="purple">
          {isCreate ? t('Workflow.createMode') : t('Workflow.editMode')}
        </EditModeLabel>
      )}
      {/* 실행 상태 라벨 */}
      {execution && (
        <ExecutionStatusLabel variant="outline" color="purple" $isBlinking={isBlinkingStatus(execution.status)}>
          {execution.status}
        </ExecutionStatusLabel>
      )}
    </Viewer>
  );
});

export const WorkflowViewer = observer((props: Props) => {
  return (
    <ReactFlowProvider>
      <WorkflowViewerContent {...props} />
    </ReactFlowProvider>
  );
});

const Viewer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  height: 100%;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  background: url(${DotBG}) ${props => props.theme.colors['bg-gray-lighter']};
  background-size: 1500px;
  background-repeat: repeat;
  padding: 44px 0px 28px 0px;
  position: relative;
`;

const StyledReactFlow = styled(ReactFlow)`
  .react-flow__handle-top {
    top: -5px;
  }
  .react-flow__handle-bottom {
    bottom: -5px;
  }
  .react-flow__edgelabel-renderer {
    pointer-events: none;
  }
  .react-flow__edgelabel-renderer > * {
    pointer-events: all;
  }
`;

const EditModeLabel = styled(Label)`
  ${fonts.Headline9};
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 1px 10px;
  border-radius: 3px;
`;

const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

const ExecutionStatusLabel = styled(Label)<{ $isBlinking: boolean }>`
  ${fonts.Headline9};
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 1px 10px;
  border-radius: 3px;
  animation: ${props =>
    props.$isBlinking
      ? css`
          ${blink} 1.5s infinite
        `
      : 'none'};
`;

const FitViewButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors['border-gray-light']};
  background: ${props => props.theme.colors['bg-white']};
  color: ${props => props.theme.colors['text-gray-main']};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors['bg-primary-lighter']};
    color: ${props => props.theme.colors['text-primary']};
    border-color: ${props => props.theme.colors['border-primary']};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;
